import styled from '@emotion/styled';
import React from 'react';

import type { LensSpanProps } from '../../types';
import { getRadius, getSizeValue, u } from '../../utilities';

export type AvatarColor = 'orange' | 'blue' | 'yellow' | 'teal';

const getColorMapping = (color: AvatarColor) => {
  switch (color) {
    case 'orange':
      return {
        background: 'orangeLight',
        text: 'dangerHover',
      };
    case 'blue':
      return {
        background: 'blueLight',
        text: 'blueDark',
      };
    case 'yellow':
      return {
        background: 'yellowLight',
        text: '#9E4C00',
      };
    case 'teal':
      return {
        background: 'tealLight',
        text: 'tealDark',
      };
    default:
      return {
        background: 'orangeLight',
        text: 'dangerHover',
      };
  }
};

const getLetterSize = wrapperSize => `calc(${wrapperSize} / 2)`;

const getAvatarSize = avatarSize => {
  let width;
  let height;

  if (avatarSize === 'medium') {
    width = u(4);
    height = u(4);
  } else if (avatarSize === 'large') {
    width = u(7);
    height = u(7);
  } else {
    const sizeValue = getSizeValue(avatarSize);

    width = sizeValue;
    height = sizeValue;
  }

  const fontSize = getLetterSize(width);

  return {
    width,
    height,
    fontSize,
  };
};
const AvatarWrapper = styled.span<
  AvatarWrapperProps & { backgroundColor?: string; color?: string }
>`
  display: block;
  color: ${props =>
    props.color
      ? props.color.startsWith('#')
        ? props.color
        : `var(--lns-color-${props.color})`
      : 'var(--lns-color-blueLight)'};
  background-color: var(--lns-color-background);
  ${getRadius('full')};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-weight: var(--lns-fontWeight-bold);
  width: ${props => {
    const { width } = getAvatarSize(props.size);

    return width;
  }};
  height: ${props => {
    const { height } = getAvatarSize(props.size);

    return height;
  }};
  font-size: ${props => {
    const { fontSize } = getAvatarSize(props.size);

    return fontSize;
  }};
  position: relative;
  z-index: 0;

  &:after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    z-index: -1;
    background-color: ${props =>
      props.hasBackgroundColor &&
      (props.backgroundColor
        ? `var(--lns-color-${props.backgroundColor})`
        : 'var(--lns-color-orange)')};
  }
`;

const AvatarImage = styled.img<AvatarProps>`
  max-width: 100%;
  width: ${props => {
    const { width } = getAvatarSize(props.size);

    return width;
  }};
  height: ${props => {
    const { height } = getAvatarSize(props.size);

    return height;
  }};
  font-size: ${props => {
    const { fontSize } = getAvatarSize(props.size);

    return fontSize;
  }};
`;

const Avatar = ({
  altText = '',
  size = 4,
  letter,
  imageSrc,
  imageLoading,
  children,
  themeColor = 'blue',
  ...props
}: AvatarProps &
  React.ComponentProps<typeof AvatarWrapper> &
  Omit<
    LensSpanProps,
    keyof AvatarProps & React.ComponentProps<typeof AvatarWrapper>
  >) => {
  const getContent = () => {
    if (children) {
      return children;
    }

    if (imageSrc) {
      const height = getAvatarSize(size).height;
      const width = getAvatarSize(size).width;

      return (
        <AvatarImage
          size={size}
          alt={altText}
          src={imageSrc}
          height={height}
          width={width}
          loading={imageLoading}
        />
      );
    }

    if (letter) {
      if (altText) {
        return <span aria-label={altText}>{letter}</span>;
      }

      return letter;
    }
  };

  const hasBackgroundColor = letter && !imageSrc && !children;
  const colorMapping = getColorMapping(themeColor || 'blue');

  return (
    <AvatarWrapper
      hasBackgroundColor={hasBackgroundColor}
      size={size}
      backgroundColor={colorMapping.background}
      color={colorMapping.text}
      {...props}
    >
      {getContent()}
    </AvatarWrapper>
  );
};

type AvatarProps = {
  altText?: string;
  letter?: string;
  imageSrc?: string;
  /** Passed to the underlying `<img>` when `imageSrc` is set (e.g. `lazy` for below-the-fold lists). */
  imageLoading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
  size?: string | number;
  children?: React.ReactNode;
  height?: number;
  width?: number;
  hasBackgroundColor?: boolean;
  backgroundColor?: string;
  themeColor?: AvatarColor;
};

type AvatarWrapperProps = {
  size?: string | number;
  hasBackgroundColor?: boolean;
  themeColor?: AvatarColor;
};

export default Avatar;
